<template>
  <div id="min-max-range-slider" class="range-slider" @wheel="onInputScroll">
    <div
      class="box-container"
      :style="{
        background
      }"
    ></div>
    <input
      v-model="fromValueAsString"
      type="range"
      class="left-slider"
      :min="min"
      :max="max"
      :step="step"
      data-html2canvas-ignore
    />
    <input
      ref="toInputRef"
      v-model="toValueAsString"
      type="range"
      class="right-slider"
      :min="min"
      :max="max"
      :step="step"
      data-html2canvas-ignore
      @mousedown="onInputMouseDown"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

type Props = {
  to: number
  from: number
  min: number
  max: number
  step: number
  fromColor: string
  toColor: string
  middleColor: string
}

type Emits = {
  (e: 'updateValueFrom', number: number): void
  (e: 'updateValueTo', number: number): void
  (e: 'changeValue', value: { from: number; to: number }): void
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const toInputRef = ref<HTMLInputElement | null>(null)
const fromProp = computed(() => props.from)
const toProp = computed(() => props.to)
const fromValue = ref(props.from)
const toValue = ref(props.to)
const leftDrag = ref(false)
const dragging = ref(false)
const dragBoth = ref(false)
const dragBothDistance = ref(0)

const fromValueAsString = computed({
  get: () => fromValue.value.toString(),
  set: (value) => {
    fromValue.value = parseFloat(value)
  }
})

const toValueAsString = computed({
  get: () => toValue.value.toString(),
  set: (value) => {
    toValue.value = parseFloat(value)
  }
})

const middleBoxPosition = computed(() => {
  const position = fromValue.value
  const range = props.max - props.min
  const safePosition = Math.min(props.max, Math.max(props.min, position)) // Ensure it stays within the slider range
  return `${100 * ((safePosition - props.min) / range)}%`
})

const toBoxPosition = computed(() => {
  const position = toValue.value
  const range = props.max - props.min
  const safePosition = Math.min(props.max, Math.max(props.min, position)) // Ensure it stays within the slider range
  return `${100 * ((safePosition - props.min) / range)}%`
})

const background = computed(
  () =>
    `linear-gradient(
        to right,
        ${props.fromColor} ${middleBoxPosition.value},
        ${props.middleColor} ${middleBoxPosition.value},
        ${props.middleColor} ${toBoxPosition.value},
        ${props.toColor} ${toBoxPosition.value}
    )`
)

onMounted(() => {
  window.addEventListener('mouseup', onInputMouseUp)
  window.addEventListener('mousemove', onInputDrag)
})

onUnmounted(() => {
  window.removeEventListener('mouseup', onInputMouseUp)
  window.removeEventListener('mousemove', onInputDrag)
})

watch(fromProp, (newValue) => {
  fromValue.value = newValue
})

watch(toProp, (newValue) => {
  toValue.value = newValue
})

watch(toValue, (newValue) => {
  if (newValue < fromValue.value) {
    fromValue.value = newValue
  }
  if (newValue > props.max) {
    toValue.value = props.max
    emit('updateValueTo', props.max)
  } else {
    emit('updateValueTo', newValue)
  }
})
watch(fromValue, (newValue) => {
  if (newValue > toValue.value) {
    toValue.value = newValue
  }
  if (newValue < props.min) {
    fromValue.value = props.min
    emit('updateValueFrom', props.min)
  } else {
    emit('updateValueFrom', newValue)
  }
})

function calculateMouseValue(e: MouseEvent): number | undefined {
  if (!toInputRef.value) return
  const element = toInputRef.value
  const mx = e.clientX
  const inputLeft = element.getBoundingClientRect().left
  const inputRight = element.getBoundingClientRect().right
  const inputWidth = inputRight - inputLeft
  const mouseRelative = (mx - inputLeft) / inputWidth
  return mouseRelative * (props.max - props.min) + props.min
}

function calculateClosestValue(value: number): number {
  return Math.round(value / props.step) * props.step
}

function onInputMouseDown(e: MouseEvent) {
  const value = calculateMouseValue(e)
  if (value === undefined) return

  if (e.shiftKey) {
    dragBoth.value = true
    dragBothDistance.value = Math.abs(toValue.value - fromValue.value)
  } else {
    const toDx = Math.abs(toValue.value - value)
    const fromDx = Math.abs(fromValue.value - value)
    const closestValue = calculateClosestValue(value)

    if (toDx < fromDx) {
      leftDrag.value = false
      toValue.value = closestValue
    } else {
      leftDrag.value = true
      fromValue.value = closestValue
    }
  }
  dragging.value = true

  e.preventDefault()
}

function onInputMouseUp() {
  dragging.value = false
  dragBoth.value = false
  emit('changeValue', { from: fromValue.value, to: toValue.value })
}

function onInputDrag(e: MouseEvent) {
  if (!dragging.value) return
  const value = calculateMouseValue(e)
  if (value === undefined) return

  const closestValue = calculateClosestValue(value)

  if (dragBoth.value) {
    const from = calculateClosestValue(closestValue - dragBothDistance.value / 2)
    const to = calculateClosestValue(closestValue + dragBothDistance.value / 2)
    fromValue.value = Math.max(props.min, from)
    toValue.value = Math.min(props.max, to)
  } else {
    if (!leftDrag.value) {
      toValue.value = closestValue
    } else {
      fromValue.value = closestValue
    }
  }
}

function onInputScroll(e: Event) {
  const dy = calculateClosestValue((e as WheelEvent).deltaY / 10)
  if (fromValue.value < toValue.value || dy > 0) {
    toValue.value = calculateClosestValue(toValue.value + props.step * dy)
    fromValue.value = calculateClosestValue(fromValue.value - props.step * dy)
  }
  e.preventDefault()
  e.stopPropagation()
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.range-slider {
  position: relative;
  width: 100%;
  text-align: center;
  height: 18px;
  overflow: hidden;
  scrollbar-width: none;
}

.range-slider input[type='range'] {
  position: absolute;
  left: 0;
  bottom: 0;
  top: 0;
  width: 100%; // Make the slider take the full width
  background: transparent; // Make the slider background transparent

  /* Override default styling for transparency */
  -webkit-appearance: none;
  margin: 0;
}

.range-slider input[type='range']::-webkit-slider-runnable-track {
  width: 100%;
  height: 18px;
  position: relative;
  top: 0;
  cursor: pointer;
  background: transparent; // Make the track transparent
  border-radius: 1px;
  box-shadow: none;
  border: 0;
}

.range-slider input[type='range']::-webkit-slider-thumb {
  z-index: 1;
  position: relative;
  box-shadow: 0px 0px 0px #000;
  height: 18px;
  width: 18px;
  top: 0;
  border-radius: 50%; /* Use border-radius to create a circle */
  background: white;
  border: 1px solid #3f46d6;
  cursor: pointer;
  -webkit-appearance: none;
}

.range-slider .from-box,
.range-slider .middle-box,
.range-slider .to-box {
  position: absolute;
  left: 0;
  bottom: 0;
  top: 0;
  height: 5px;
  box-sizing: border-box;
}

.range-slider .slider {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%; // Make the sliders take the full width
  background: transparent; // Make the sliders background transparent
}

.box-container {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 5px;
  top: calc(50% - 2px);
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 3px;
}

.range-slider .middle-box {
  background-color: rgba(0, 0, 0, 0.5); // Adjust the background color or opacity as needed
}
</style>
